import React, { useState } from 'react';
import { 
    Building2, User, Settings, ChevronRight, ChevronLeft,
    Check, Loader2, Dices, Mail, Lock, Globe
} from 'lucide-react';

/**
 * OnboardingWizard - Multi-step wizard for creating a new club
 */
const OnboardingWizard = ({ email, verificationCode, onComplete }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Step 1: Club Details
    const [clubName, setClubName] = useState('');
    const [clubSlug, setClubSlug] = useState('');
    const [clubDescription, setClubDescription] = useState('');
    
    // Step 2: Admin Account
    const [adminName, setAdminName] = useState('');
    
    // Step 3: Settings
    const [language, setLanguage] = useState('fi');
    const [timezone, setTimezone] = useState('Europe/Helsinki');

    const generateSlug = (text) => {
        return text
            .toLowerCase()
            .replace(/ä/g, 'a')
            .replace(/ö/g, 'o')
            .replace(/å/g, 'a')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const handleClubNameChange = (e) => {
        const name = e.target.value;
        setClubName(name);
        setClubSlug(generateSlug(name));
    };

    const validateStep = () => {
        setError('');
        
        switch (step) {
            case 1:
                if (!clubName.trim()) {
                    setError('Anna klubille nimi');
                    return false;
                }
                if (!clubSlug.trim()) {
                    setError('Anna klubille URL-osoite');
                    return false;
                }
                break;
            case 2:
                if (!adminName.trim()) {
                    setError('Anna nimesi');
                    return false;
                }
                break;
            default:
                break;
        }
        return true;
    };

    const nextStep = () => {
        if (validateStep()) {
            setStep(s => s + 1);
        }
    };

    const prevStep = () => {
        setError('');
        setStep(s => s - 1);
    };

    const handleSubmit = async () => {
        if (!validateStep()) return;
        
        setLoading(true);
        setError('');
        
        try {
            const res = await fetch('/api/onboarding/complete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    verificationCode,
                    club: {
                        name: clubName,
                        slug: clubSlug,
                        description: clubDescription,
                        language,
                        timezone
                    },
                    admin: {
                        name: adminName,
                        email
                    }
                })
            });
            
            if (res.ok) {
                const data = await res.json();
                onComplete(data);
            } else {
                const data = await res.json();
                setError(data.error || 'Jokin meni pieleen');
            }
        } catch (err) {
            setError('Yhteysvirhe. Yritä uudelleen.');
        } finally {
            setLoading(false);
        }
    };

    const steps = [
        { num: 1, title: 'Klubin tiedot', icon: Building2 },
        { num: 2, title: 'Admin-tili', icon: User },
        { num: 3, title: 'Asetukset', icon: Settings }
    ];

    return (
        <div className="onboarding-wizard">
            <div className="wizard-container">
                {/* Header */}
                <div className="wizard-header">
                    <Dices size={32} />
                    <h1>Tervetuloa!</h1>
                    <p>Luodaan sinulle oma lautapeliklubi</p>
                </div>

                {/* Progress Steps */}
                <div className="wizard-steps">
                    {steps.map((s, i) => (
                        <React.Fragment key={s.num}>
                            <div className={`wizard-step ${step === s.num ? 'active' : ''} ${step > s.num ? 'completed' : ''}`}>
                                <div className="step-circle">
                                    {step > s.num ? <Check size={16} /> : <s.icon size={16} />}
                                </div>
                                <span className="step-title">{s.title}</span>
                            </div>
                            {i < steps.length - 1 && <div className={`step-line ${step > s.num ? 'completed' : ''}`} />}
                        </React.Fragment>
                    ))}
                </div>

                {/* Step Content */}
                <div className="wizard-content">
                    {step === 1 && (
                        <div className="step-content">
                            <h2><Building2 size={20} /> Klubin tiedot</h2>
                            <p className="step-description">
                                Kerro meille lautapeliklubistasi
                            </p>
                            
                            <div className="form-group">
                                <label>Klubin nimi *</label>
                                <input 
                                    type="text"
                                    value={clubName}
                                    onChange={handleClubNameChange}
                                    placeholder="esim. Turun Noppanikkari"
                                    autoFocus
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>URL-osoite *</label>
                                <div className="slug-input">
                                    <span>boardgameleague.app/</span>
                                    <input 
                                        type="text"
                                        value={clubSlug}
                                        onChange={e => setClubSlug(e.target.value)}
                                        placeholder="turun-noppanikkari"
                                    />
                                </div>
                            </div>
                            
                            <div className="form-group">
                                <label>Kuvaus (valinnainen)</label>
                                <textarea 
                                    value={clubDescription}
                                    onChange={e => setClubDescription(e.target.value)}
                                    placeholder="Lyhyt kuvaus klubistanne..."
                                    rows={3}
                                />
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="step-content">
                            <h2><User size={20} /> Admin-tilisi</h2>
                            <p className="step-description">
                                Sinusta tulee klubin ensimmäinen admin
                            </p>
                            
                            <div className="form-group">
                                <label>Nimesi *</label>
                                <input 
                                    type="text"
                                    value={adminName}
                                    onChange={e => setAdminName(e.target.value)}
                                    placeholder="Miten muut pelaajat tunnistavat sinut?"
                                    autoFocus
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Sähköposti</label>
                                <div className="readonly-field">
                                    <Mail size={16} />
                                    <span>{email}</span>
                                    <Check size={14} className="verified" />
                                </div>
                            </div>
                            
                            <div className="info-box">
                                <Lock size={16} />
                                <span>
                                    Kirjaudut aina magic link -linkillä. 
                                    Ei salasanoja muistettavaksi!
                                </span>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="step-content">
                            <h2><Settings size={20} /> Alkuasetukset</h2>
                            <p className="step-description">
                                Voit muuttaa näitä myöhemmin asetuksista
                            </p>
                            
                            <div className="form-group">
                                <label><Globe size={16} /> Kieli</label>
                                <select value={language} onChange={e => setLanguage(e.target.value)}>
                                    <option value="fi">Suomi</option>
                                    <option value="en">English</option>
                                    <option value="sv">Svenska</option>
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label>Aikavyöhyke</label>
                                <select value={timezone} onChange={e => setTimezone(e.target.value)}>
                                    <option value="Europe/Helsinki">Helsinki (UTC+2/+3)</option>
                                    <option value="Europe/Stockholm">Tukholma (UTC+1/+2)</option>
                                    <option value="Europe/London">Lontoo (UTC+0/+1)</option>
                                </select>
                            </div>

                            {/* Summary */}
                            <div className="summary-box">
                                <h4>Yhteenveto</h4>
                                <div className="summary-row">
                                    <span>Klubi:</span>
                                    <strong>{clubName}</strong>
                                </div>
                                <div className="summary-row">
                                    <span>URL:</span>
                                    <code>/{clubSlug}</code>
                                </div>
                                <div className="summary-row">
                                    <span>Admin:</span>
                                    <strong>{adminName}</strong>
                                </div>
                            </div>
                        </div>
                    )}

                    {error && <p className="error-text">{error}</p>}
                </div>

                {/* Footer */}
                <div className="wizard-footer">
                    {step > 1 && (
                        <button className="btn-secondary" onClick={prevStep}>
                            <ChevronLeft size={18} /> Takaisin
                        </button>
                    )}
                    
                    <div style={{ flex: 1 }} />
                    
                    {step < 3 ? (
                        <button className="btn-primary" onClick={nextStep}>
                            Seuraava <ChevronRight size={18} />
                        </button>
                    ) : (
                        <button className="btn-primary success" onClick={handleSubmit} disabled={loading}>
                            {loading ? <Loader2 size={18} className="spin" /> : <Check size={18} />}
                            Luo klubi
                        </button>
                    )}
                </div>
            </div>

            <style>{`
                .onboarding-wizard {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 2rem;
                    background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%);
                }

                .wizard-container {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 20px;
                    width: 100%;
                    max-width: 560px;
                    overflow: hidden;
                }

                .wizard-header {
                    text-align: center;
                    padding: 2rem;
                    background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.1));
                    border-bottom: 1px solid #30363d;
                    color: #f0f6fc;
                }

                .wizard-header svg {
                    color: #a78bfa;
                    margin-bottom: 0.5rem;
                }

                .wizard-header h1 {
                    margin: 0 0 0.25rem 0;
                    font-size: 1.5rem;
                }

                .wizard-header p {
                    margin: 0;
                    color: #8b949e;
                }

                .wizard-steps {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1.5rem;
                    border-bottom: 1px solid #30363d;
                }

                .wizard-step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                }

                .step-circle {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #21262d;
                    border: 2px solid #374151;
                    color: #8b949e;
                    transition: all 0.3s;
                }

                .wizard-step.active .step-circle {
                    background: rgba(139, 92, 246, 0.2);
                    border-color: #8b5cf6;
                    color: #a78bfa;
                }

                .wizard-step.completed .step-circle {
                    background: #22c55e;
                    border-color: #22c55e;
                    color: white;
                }

                .step-title {
                    font-size: 0.75rem;
                    color: #6b7280;
                }

                .wizard-step.active .step-title {
                    color: #a78bfa;
                }

                .wizard-step.completed .step-title {
                    color: #22c55e;
                }

                .step-line {
                    width: 60px;
                    height: 2px;
                    background: #374151;
                    margin: 0 0.5rem;
                    margin-bottom: 1.5rem;
                    transition: background 0.3s;
                }

                .step-line.completed {
                    background: #22c55e;
                }

                .wizard-content {
                    padding: 2rem;
                }

                .step-content h2 {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin: 0 0 0.5rem 0;
                    color: #f0f6fc;
                    font-size: 1.25rem;
                }

                .step-description {
                    color: #8b949e;
                    margin-bottom: 1.5rem;
                }

                .form-group {
                    margin-bottom: 1.25rem;
                }

                .form-group label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 0.5rem;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-group input,
                .form-group select,
                .form-group textarea {
                    width: 100%;
                    padding: 0.75rem 1rem;
                    background: #0d1117;
                    border: 2px solid #30363d;
                    border-radius: 10px;
                    color: #f0f6fc;
                    font-size: 1rem;
                    transition: border-color 0.2s;
                }

                .form-group input:focus,
                .form-group select:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #8b5cf6;
                }

                .slug-input {
                    display: flex;
                    align-items: center;
                    background: #0d1117;
                    border: 2px solid #30363d;
                    border-radius: 10px;
                    overflow: hidden;
                }

                .slug-input span {
                    padding: 0.75rem;
                    background: #21262d;
                    color: #8b949e;
                    font-size: 0.9rem;
                    white-space: nowrap;
                }

                .slug-input input {
                    border: none;
                    border-radius: 0;
                }

                .readonly-field {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem 1rem;
                    background: #0d1117;
                    border: 2px solid #30363d;
                    border-radius: 10px;
                    color: #8b949e;
                }

                .readonly-field .verified {
                    color: #22c55e;
                    margin-left: auto;
                }

                .info-box {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 1rem;
                    background: rgba(59, 130, 246, 0.1);
                    border: 1px solid rgba(59, 130, 246, 0.3);
                    border-radius: 10px;
                    color: #60a5fa;
                    font-size: 0.9rem;
                }

                .summary-box {
                    background: rgba(139, 92, 246, 0.1);
                    border: 1px solid rgba(139, 92, 246, 0.3);
                    border-radius: 12px;
                    padding: 1.25rem;
                    margin-top: 1.5rem;
                }

                .summary-box h4 {
                    margin: 0 0 1rem 0;
                    color: #a78bfa;
                    font-size: 0.9rem;
                }

                .summary-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 0.5rem 0;
                    border-bottom: 1px solid rgba(139, 92, 246, 0.2);
                    font-size: 0.9rem;
                }

                .summary-row:last-child {
                    border-bottom: none;
                }

                .summary-row span {
                    color: #8b949e;
                }

                .summary-row strong {
                    color: #f0f6fc;
                }

                .summary-row code {
                    background: rgba(0, 0, 0, 0.3);
                    padding: 0.2rem 0.5rem;
                    border-radius: 4px;
                    color: #a78bfa;
                }

                .error-text {
                    color: #ef4444;
                    font-size: 0.9rem;
                    margin-top: 1rem;
                }

                .wizard-footer {
                    display: flex;
                    gap: 1rem;
                    padding: 1.5rem 2rem;
                    border-top: 1px solid #30363d;
                    background: #0d1117;
                }

                .btn-primary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                    border: none;
                    border-radius: 10px;
                    color: white;
                    font-weight: 600;
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                }

                .btn-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 20px rgba(139, 92, 246, 0.3);
                }

                .btn-primary.success {
                    background: linear-gradient(135deg, #22c55e, #16a34a);
                }

                .btn-primary:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .btn-secondary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    background: #21262d;
                    border: 1px solid #30363d;
                    border-radius: 10px;
                    color: #c9d1d9;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .btn-secondary:hover {
                    background: #30363d;
                }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                @media (max-width: 600px) {
                    .onboarding-wizard {
                        padding: 0;
                    }

                    .wizard-container {
                        border-radius: 0;
                        min-height: 100vh;
                    }

                    .step-title {
                        display: none;
                    }

                    .step-line {
                        margin-bottom: 0;
                    }
                }
            `}</style>
        </div>
    );
};

export default OnboardingWizard;

